import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import spellCatalog from '../../../data/spells.json';
import {
  STYLE_CANTRIP_COUNT, getCantripStyle, fightingStyleClassLabel, getFightingStyleOptions,
  getClassFightingStyle, canChangeClassFightingStyle,
  getAdditionalFightingStyle, getAdditionalFightingStyleOptions,
} from '../../../shared/rules/fightingStyle.mjs';
import { canChangeAdditionalFightingStyle } from '../../../shared/rules/champion.mjs';

const spellIdFromName = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const cantripsFor = (spellList) => spellCatalog
  .filter((spell) => Number(spell.level) === 0 && (spell.classes || []).map((entry) => String(entry).toLowerCase()).includes(spellList))
  .map((spell) => ({ id: spellIdFromName(spell.name), name: spell.name }))
  .sort((a, b) => a.name.localeCompare(b.name));
/** Cantrips a cantrip Fighting Style can teach, by spell list (Druidic Warrior: druid, Blessed Warrior: cleric). */
export const CLASS_CANTRIPS = Object.freeze({ druid: cantripsFor('druid'), cleric: cantripsFor('cleric') });
export const DRUID_CANTRIPS = CLASS_CANTRIPS.druid;

// `slot: 'additional'` edits the Champion's Additional Fighting Style (Fighter 7): a second style feat, not the class style.
export default function FightingStyleChooser({ form, characterId, onCharacterChange, classId = 'fighter', slot = 'class' }) {
  const additional = slot === 'additional';
  const field = additional ? 'additionalFightingStyle' : 'fightingStyle';
  const options = additional ? getAdditionalFightingStyleOptions(form) : getFightingStyleOptions(classId);
  const label = fightingStyleClassLabel(classId);
  const currentOf = character => (additional ? getAdditionalFightingStyle(character) : getClassFightingStyle(character, classId));
  const current = currentOf(form);
  const [selected, setSelected] = useState(current?.id || options[0]?.id || 'defense');
  const [cantrips, setCantrips] = useState(() => (current?.cantrips?.length ? [...current.cantrips] : Array(STYLE_CANTRIP_COUNT).fill('')));
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const effective = saved ? { ...form, classState: { ...form.classState, [classId]: { ...form.classState?.[classId], [field]: saved } } } : form;
  // Druidic Warrior (Ranger) and Blessed Warrior (Paladin) also choose two cantrips from another class's list.
  const cantripStyle = getCantripStyle(selected);
  const cantripsReady = !cantripStyle || (cantrips.every(Boolean) && new Set(cantrips).size === cantrips.length);
  const choose = async () => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/classes/${classId}/${additional ? 'additional-fighting-style' : 'fighting-style'}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styleId: selected, expectedStyleId: currentOf(effective)?.id || null, ...(cantripStyle ? { cantrips } : {}) }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Could not save Fighting Style.');
      setSaved(result[field]);
      onCharacterChange?.(previous => ({ ...previous, classState: { ...previous.classState, [classId]: { ...previous.classState?.[classId], [field]: result[field] } } }));
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  };
  const locked = additional ? !canChangeAdditionalFightingStyle(effective) : !canChangeClassFightingStyle(effective, classId);
  return <div className="fighting-style-chooser">
    <p className="feature-sheet-hint">{locked
      ? `You can replace this style the next time you gain a ${label} level.`
      : additional ? `A second Fighting Style feat from your Champion subclass, different from your Fighter style. You can replace it when you gain a ${label} level.`
        : `This class choice does not use your level-4 feat slot. You can replace it when you gain a ${label} level.`}</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {saved && <Alert variant="success">Fighting Style saved.</Alert>}
    <Form.Label htmlFor={`${classId}-${slot}-style`}>{additional ? 'Additional Fighting Style' : 'Fighting Style'}</Form.Label>
    <Form.Select id={`${classId}-${slot}-style`} value={selected} disabled={saving || locked} onChange={event => setSelected(event.target.value)}>
      {options.map(style => <option key={style.id} value={style.id}>{style.name}</option>)}
    </Form.Select>
    <p className="fighting-style-description">{options.find(style => style.id === selected)?.description}</p>
    {cantripStyle && <div className="fighting-style-cantrips">
      {cantrips.map((value, index) => (
        <Form.Group key={index} controlId={`${classId}-druidic-cantrip-${index}`}>
          <Form.Label>{`${cantripStyle.listLabel} cantrip ${index + 1}`}</Form.Label>
          <Form.Select value={value} disabled={saving || locked} onChange={event => setCantrips(previous => previous.map((entry, i) => (i === index ? event.target.value : entry)))}>
            <option value="">Choose a cantrip</option>
            {CLASS_CANTRIPS[cantripStyle.spellList].map(spell => <option key={spell.id} value={spell.id} disabled={cantrips.includes(spell.id) && value !== spell.id}>{spell.name}</option>)}
          </Form.Select>
        </Form.Group>
      ))}
    </div>}
    <div className="fighting-style-actions"><Button size="sm" onClick={choose} disabled={saving || locked || !cantripsReady}>{saving ? 'Saving…' : 'Save Fighting Style'}</Button></div>
  </div>;
}
